'use client';

import { PlotlyChart } from './PlotlyChart';
import { type MetricsSample } from '../types';

interface Props { samples: MetricsSample[]; loading?: boolean }

type InfraField = 'redis_cpu' | 'redis_memory' | 'opensearch_cpu' | 'opensearch_memory' | 'opensearch_queues';

const INFRA_FIELDS: InfraField[] = [
  'redis_cpu',
  'redis_memory',
  'opensearch_cpu',
  'opensearch_memory',
  'opensearch_queues',
];

const FIELD_PALETTE: Record<InfraField, string> = {
  redis_cpu:         '#ff6b6b',
  redis_memory:      '#feca57',
  opensearch_cpu:    '#48dbfb',
  opensearch_memory: '#54a0ff',
  opensearch_queues: '#5f27cd',
};

const FIELD_LABELS: Record<InfraField, string> = {
  redis_cpu:         'RedisCPU',
  redis_memory:      'RedisMemory',
  opensearch_cpu:    'OpenSearchCPU',
  opensearch_memory: 'OpenSearchMemory',
  opensearch_queues: 'OpenSearchQueues',
};

export function InfraProbesChart({ samples, loading }: Props) {
  // For each ts_ms, average the values across pods (these are shared
  // infra metrics, so any pod's reading should be ~the same).
  const sortedSamples = [...samples].sort((a, b) => a.ts_ms - b.ts_ms);
  const byField = new Map<InfraField, { x: Date[]; y: number[]; pending: Map<number, { sum: number; n: number }> }>();
  for (const field of INFRA_FIELDS) {
    byField.set(field, { x: [], y: [], pending: new Map() });
  }
  for (const s of sortedSamples) {
    for (const field of INFRA_FIELDS) {
      const v = s[field];
      if (v < 0) continue;
      const slot = byField.get(field)!;
      const acc = slot.pending.get(s.ts_ms) ?? { sum: 0, n: 0 };
      acc.sum += v;
      acc.n += 1;
      slot.pending.set(s.ts_ms, acc);
    }
  }
  for (const slot of byField.values()) {
    const sortedTs = Array.from(slot.pending.keys()).sort((a, b) => a - b);
    for (const ts of sortedTs) {
      const acc = slot.pending.get(ts)!;
      slot.x.push(new Date(ts));
      slot.y.push(acc.sum / acc.n);
    }
  }

  const traces = INFRA_FIELDS.map((field) => {
    const slot = byField.get(field)!;
    return {
      x: slot.x,
      y: slot.y,
      type: 'scatter' as const,
      mode: 'lines' as const,
      name: FIELD_LABELS[field],
      line: { color: FIELD_PALETTE[field], width: 1.5 },
      connectgaps: false,
    };
  });

  return <PlotlyChart traces={traces} yTitle="%" yRange={[0, 110]} perPod={false} showLegend loading={loading} />;
}
